/**
 * Guest Loyalty Wallet — public premium membership portal.
 *
 * URL: /loyalty/:slug
 *
 * ITER289 redesign: replaced the SaaS-dashboard look with a luxury
 * hospitality membership experience (Royal Blue + Gold reference on
 * pure white). All colour comes from CSS variables mounted by
 * <GuestLoyaltyThemeProvider> so future tenants can rebrand without
 * touching JSX. No new API — same endpoints and same data model as
 * before.
 *
 * Auth: DB-backed opaque tokens via `X-Loyalty-Session` header.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, LogOut, Mail, QrCode as QrIcon, X,
         Sparkles, Ticket, Clock, User as UserIcon, FileText } from 'lucide-react';

import { GuestLoyaltyThemeProvider } from '../components/loyalty/ThemeProvider';
import {
  LoyaltyHero, MembershipCard, VoucherCard,
  BottomNav, EmptyState,
} from '../components/loyalty/PremiumComponents';
import GuestVoucherWallet from '../components/loyalty/GuestVoucherWallet';
import GuestBrandingHeader from '../components/loyalty/GuestBrandingHeader';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'loyalty_session_token';

/* -----------------------------------------------------------------
 * Root
 * ----------------------------------------------------------------- */
export default function GuestLoyaltyWallet() {
  const { slug } = useParams();
  const [outlet, setOutlet] = useState(null);
  const [config, setConfig] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem(SESSION_KEY) || '');
  const [wallet, setWallet] = useState(null);
  const [loadingOutlet, setLoadingOutlet] = useState(true);
  const [outletError, setOutletError] = useState('');
  const [activeTab, setActiveTab] = useState('home');
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/loyalty/guest/outlet-by-slug/${slug}`)
      .then((res) => { setOutlet(res.data.outlet); setConfig(res.data.config); })
      .catch((e) => {
        setOutletError(e?.response?.data?.detail || 'This loyalty page is not available.');
      })
      .finally(() => setLoadingOutlet(false));
  }, [slug]);

  const loadWallet = useCallback(async (t) => {
    try {
      const res = await axios.get(`${API}/api/loyalty/guest/wallet`, {
        headers: { 'X-Loyalty-Session': t },
      });
      setWallet(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(SESSION_KEY);
        setToken(''); setWallet(null);
      }
    }
  }, []);

  useEffect(() => { if (token) loadWallet(token); }, [token, loadWallet]);

  // B4 — auto-refresh while visible.
  useEffect(() => {
    if (!token) return;
    const tick = () => {
      if (document.visibilityState === 'visible') loadWallet(token);
    };
    const id = setInterval(tick, 30_000);
    const onVis = () => { if (document.visibilityState === 'visible') loadWallet(token); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [token, loadWallet]);

  const logout = async () => {
    try {
      await axios.post(`${API}/api/loyalty/guest/logout`, {}, {
        headers: { 'X-Loyalty-Session': token },
      });
    } catch { /* ignore */ }
    sessionStorage.removeItem(SESSION_KEY);
    setToken(''); setWallet(null);
    toast.success('Signed out');
  };

  /* Loading / error states */
  if (loadingOutlet) {
    return (
      <GuestLoyaltyThemeProvider mode="dark"
                                  className="min-h-screen flex items-center justify-center">
        <div className="text-sm" style={{ color: 'var(--ink-muted)' }}>Loading…</div>
      </GuestLoyaltyThemeProvider>
    );
  }
  if (!outlet) {
    const isPublishIssue = outletError && /published|not available|feature/i.test(outletError);
    return (
      <GuestLoyaltyThemeProvider mode="dark"
                                  className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center rounded-3xl p-8 space-y-4"
             style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-lg)',
                      border: '1px solid var(--border-default)' }}
             data-testid="guest-outlet-unavailable">
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
               style={{ background: 'rgba(244, 63, 94, 0.14)' }}>
            <ShieldCheck className="w-7 h-7 text-rose-400" />
          </div>
          <h1 className="font-serif text-2xl" style={{ color: 'var(--ink)' }}>
            Membership Unavailable
          </h1>
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}
             data-testid="guest-outlet-error-detail">
            {outletError || 'This loyalty page could not be loaded.'}
          </p>
          {isPublishIssue && (
            <p className="text-xs pt-3 border-t" style={{ color: 'var(--ink-muted)',
                                                           borderColor: 'var(--border-subtle)' }}>
              If you are the outlet owner, enable the public loyalty page in
              Admin → Loyalty → Outlet Config.
            </p>
          )}
        </div>
      </GuestLoyaltyThemeProvider>
    );
  }

  // ITER294 — pipe the outlet's persisted theme (primary/secondary/
  // background + logo) into the theme provider. Missing fields fall
  // through to the system default inside the provider.
  const themeConfig = config || null;

  return (
    <GuestLoyaltyThemeProvider config={themeConfig} mode="dark"
                                className="min-h-screen">
      {/* ITER305-refine · Luxury font pairing is loaded ONCE from
           /app/frontend/public/index.html <head> so document.head has
           exactly one fonts.googleapis.com <link>. The <style> below
           just maps our CSS classes to those families — no <link> in
           JSX (React 18 does not hoist links to <head>). */}
      <style>{`
        [data-loyalty-theme-root] {
          font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
          -webkit-font-smoothing: antialiased;
        }
        [data-loyalty-theme-root] .font-serif,
        [data-loyalty-theme-root] h1,
        [data-loyalty-theme-root] h2,
        [data-loyalty-theme-root] h3 {
          font-family: 'Playfair Display', ui-serif, Georgia, serif;
          letter-spacing: 0;
        }
        [data-loyalty-theme-root] .font-sans {
          font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
        }
        /* Anything visually "labelly" — uppercase, tracked wide, or
             a filter chip — reads as a label and uses Montserrat. */
        [data-loyalty-theme-root] .uppercase,
        [data-loyalty-theme-root] [data-testid^="wallet-chip-"],
        [data-loyalty-theme-root] [data-testid^="wallet-chip-"] * {
          font-family: 'Montserrat', ui-sans-serif, system-ui, sans-serif;
          font-feature-settings: 'ss01';
        }
        [data-loyalty-theme-root] .font-mono,
        [data-loyalty-theme-root] code,
        [data-loyalty-theme-root] pre,
        [data-loyalty-theme-root] [data-testid="membership-card-number"] {
          font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular,
                       Menlo, monospace;
        }
      `}</style>

      {/* ITER305 · Persistent branding — visible on every tab so
           guests never see a mystery-meat white screen when tab-
           switching. Rendered before the max-width wrapper so it
           spans the viewport on tablet/desktop. */}
      {token && wallet && (
        <GuestBrandingHeader
          outletName={outlet.name}
          programName={config?.program_name}
          logoUrl={config?.logo_url}
          memberName={wallet.member?.name}
        />
      )}

      <div className="max-w-[480px] mx-auto pb-28"
           data-testid="guest-loyalty-page">
        {/* Signed-out state — show hero + login card, no bottom nav. */}
        {!token && (
          <>
            <LoyaltyHero
              outletName={outlet.name}
              welcomeMessage={config?.welcome_message}
              programName={config?.program_name}
            />
            <div className="px-6 mt-4">
              <GuestLogin outletId={outlet.id}
                          onLoggedIn={(t) => {
                            sessionStorage.setItem(SESSION_KEY, t);
                            setToken(t);
                          }} />
              <div className="text-center text-[11px] mt-8"
                   style={{ color: 'var(--ink-muted)' }}>
                <ShieldCheck className="w-3 h-3 inline mr-1" />
                Secure guest session — never share your code
              </div>
            </div>
          </>
        )}

        {token && wallet && (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
            >
              {activeTab === 'home' && (
                <HomeTab wallet={wallet} outlet={outlet} config={config}
                          onOpenQr={() => setQrOpen(true)} />
              )}
              {activeTab === 'vouchers' && (
                <VouchersTab wallet={wallet} outlet={outlet} config={config}
                              sessionToken={token}
                              onRefresh={() => loadWallet(token)} />
              )}
              {activeTab === 'qr' && (
                <QrTab wallet={wallet} outlet={outlet} />
              )}
              {activeTab === 'history' && (
                <HistoryTab wallet={wallet} />
              )}
              {activeTab === 'terms' && (
                <TermsTab wallet={wallet} />
              )}
              {activeTab === 'profile' && (
                <ProfileTab wallet={wallet} outlet={outlet}
                            config={config} onLogout={logout} />
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {token && wallet && (
        <BottomNav active={activeTab} onNavigate={setActiveTab} />
      )}

      {/* QR overlay (opened from Membership Card header button) */}
      <AnimatePresence>
        {qrOpen && token && wallet && (
          <QrOverlay wallet={wallet} outlet={outlet} onClose={() => setQrOpen(false)} />
        )}
      </AnimatePresence>
    </GuestLoyaltyThemeProvider>
  );
}

/* -----------------------------------------------------------------
 * Login card
 * ----------------------------------------------------------------- */
function GuestLogin({ outletId, onLoggedIn }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);

  const request = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/loyalty/guest/otp/request`, {
        outlet_id: outletId, email,
      });
      setStep('otp');
      toast.success('If your email is registered, a code has been sent. Please check your inbox.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) toast.error(detail || 'Too many attempts — please try again shortly.');
      else toast.error(detail || 'Request failed');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/loyalty/guest/otp/verify`, {
        outlet_id: outletId, email, otp,
      });
      onLoggedIn(res.data.token);
      toast.success(`Welcome, ${res.data.member.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid code');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl p-6 md:p-7"
         style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-lg)',
                  border: '1px solid var(--border-default)' }}
         data-testid="guest-login-card">
      {step === 'email' ? (
        <>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-2"
               style={{ color: 'var(--ink-muted)' }}>
            Member Sign-In
          </div>
          <div className="font-serif text-2xl mb-1" style={{ color: 'var(--ink)' }}>
            Enter your registered email
          </div>
          <div className="text-sm mb-5" style={{ color: 'var(--ink-muted)' }}>
            We&apos;ll send a single-use code to your inbox.
          </div>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--ink-muted)' }} />
            <input type="email" value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && request()}
                   placeholder="you@example.com"
                   data-testid="guest-email"
                   className="w-full h-12 pl-11 pr-4 rounded-2xl text-sm outline-none
                              transition-shadow focus:shadow-md"
                   style={{ background: 'var(--surface)',
                            border: '1px solid var(--border-default)',
                            color: 'var(--ink)' }} />
          </div>
          <button
            onClick={request}
            disabled={busy || !email.trim()}
            data-testid="guest-request-otp"
            className="w-full mt-4 h-12 rounded-2xl text-sm font-semibold
                       transition-transform active:scale-[0.98] disabled:opacity-60"
            style={{ background: 'var(--gold)', color: '#0B2A5B' }}>
            {busy ? 'Sending…' : 'Send login code'}
          </button>
          <p className="text-[11px] mt-4" style={{ color: 'var(--ink-muted)' }}>
            If your email is not registered, please ask the concierge to update your details.
          </p>
        </>
      ) : (
        <>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-2"
               style={{ color: 'var(--ink-muted)' }}>
            Verification
          </div>
          <div className="font-serif text-2xl mb-1" style={{ color: 'var(--ink)' }}>
            Enter your login code
          </div>
          <div className="text-sm mb-5" style={{ color: 'var(--ink-muted)' }}>
            Sent to <span style={{ color: 'var(--ink)' }}>{email}</span>. Check spam if you don&apos;t see it.
          </div>
          <input value={otp} onChange={(e) => setOtp(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && verify()}
                 placeholder="000000" inputMode="numeric" maxLength={6}
                 data-testid="guest-otp"
                 className="w-full h-14 rounded-2xl text-center text-2xl tracking-[0.5em] font-medium outline-none focus:shadow-md"
                 style={{ background: 'var(--surface)',
                          border: '1px solid var(--border-default)',
                          color: 'var(--ink)' }} />
          <div className="flex gap-2 mt-4">
            <button
              onClick={verify}
              disabled={busy || otp.length < 4}
              data-testid="guest-verify-otp"
              className="flex-1 h-12 rounded-2xl text-sm font-semibold
                         transition-transform active:scale-[0.98] disabled:opacity-60"
              style={{ background: 'var(--gold)', color: '#0B2A5B' }}>
              {busy ? 'Verifying…' : 'Verify'}
            </button>
            <button onClick={() => { setStep('email'); setOtp(''); }}
                    className="px-4 h-12 rounded-2xl text-sm"
                    style={{ color: 'var(--ink-muted)' }}>
              Back
            </button>
          </div>
          <button onClick={request} disabled={busy}
                  className="text-xs mt-4 underline"
                  style={{ color: 'var(--ink-muted)' }}>
            Resend code
          </button>
        </>
      )}
    </div>
  );
}

/* -----------------------------------------------------------------
 * Tabs
 * ----------------------------------------------------------------- */
function firstName(name) {
  return (name || '').trim().split(/\s+/)[0] || '';
}

function HomeTab({ wallet, outlet, config, onOpenQr }) {
  const activeCards = wallet.cards.filter((c) => c.status === 'active');
  const card = activeCards[0];

  return (
    <div>
      {/* ITER305 · BrandStripHeader removed — replaced by the
           persistent GuestBrandingHeader that sits above every tab.
           ITER305-refine · "Your Privileges" preview removed; vouchers
           are exclusively on the Privileges tab now. */}
      <LoyaltyHero
        outletName={outlet.name}
        welcomeMessage={config?.welcome_message}
        memberFirstName={firstName(wallet.member.name)}
        programName={config?.program_name}
      />

      <div className="px-6">
        {card ? (
          <MembershipCard
            card={card}
            memberName={wallet.member.name}
            memberIdPublic={wallet.member.member_id_public}
            tier={card.tier || card.kit_tier}
            onOpenQr={onOpenQr}
          />
        ) : (
          <EmptyState
            title="No Active Membership"
            description="Your membership has ended. Please speak with the concierge to renew."
            icon={Sparkles}
            testId="no-active-card"
          />
        )}

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-2 mt-6">
          <StatChip label="Available" value={wallet.stats?.by_status?.unused || 0} />
          <StatChip label="Enjoyed" value={wallet.stats?.by_status?.redeemed || 0} />
          <StatChip label="Total" value={wallet.stats?.total || 0} />
        </div>

        {/* ITER305-refine · The Home tab intentionally ends here.
             The "Your Privileges" preview was removed so Home reads as
             a clean, uncluttered membership card. Vouchers now live
             exclusively on the Privileges tab. */}
      </div>
    </div>
  );
}

function VouchersTab({ wallet, outlet, config, sessionToken, onRefresh }) {
  const memberStatus = (wallet.member?.status || 'active').toLowerCase();
  const isOnHold = memberStatus === 'on_hold';
  const isRestricted = memberStatus === 'blacklisted';
  return (
    <div className="pt-8" data-testid="vouchers-tab">
      <div className="px-6">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-1"
             style={{ color: 'var(--ink-muted)' }}>
          Your Privileges
        </div>
        <div className="font-serif text-3xl mb-2" style={{ color: 'var(--ink)' }}>
          All Privileges
        </div>

        {(isOnHold || isRestricted) && (
          <div className="rounded-2xl p-4 mb-4"
               data-testid="member-status-banner"
               style={{
                 background: isRestricted ? 'rgba(220, 38, 38, 0.12)' : 'rgba(217, 119, 6, 0.12)',
                 border: `1px solid ${isRestricted ? 'rgba(220, 38, 38, 0.3)' : 'rgba(217, 119, 6, 0.3)'}`,
                 color: '#FFFFFF',
               }}>
            <div className="font-serif text-lg mb-1">
              {isOnHold ? 'Membership Temporarily On Hold' : 'Membership Access Restricted'}
            </div>
            <div className="text-[13px]" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Your membership benefits are currently unavailable.
              Please contact {isOnHold ? 'the outlet/team' : 'our team'} for assistance.
            </div>
          </div>
        )}
      </div>

      {/* ITER303 · Premium Guest Wallet split-view.
          Consumes the shared <VoucherCard> so what the admin designed
          in the Studio is exactly what the guest sees. */}
      <GuestVoucherWallet
        vouchers={wallet.vouchers}
        outlet={outlet}
        config={config}
        sessionToken={sessionToken}
        onRefresh={onRefresh}
        memberStatus={memberStatus}
      />
    </div>
  );
}

function QrTab({ wallet, outlet }) {
  const card = wallet.cards.find((c) => c.status === 'active') || wallet.cards[0];
  return (
    <div className="pt-10 px-6 text-center" data-testid="qr-tab">
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-1"
           style={{ color: 'var(--ink-muted)' }}>
        Digital Membership
      </div>
      <div className="font-serif text-3xl mb-6" style={{ color: 'var(--ink)' }}>
        Present at the Restaurant
      </div>
      <div className="mx-auto rounded-[24px] p-6 max-w-[320px]"
           style={{ background: 'var(--surface)', border: '1px solid var(--border-default)',
                    boxShadow: 'var(--shadow-lg)' }}>
        <QrPlaceholder text={card?.card_number || wallet.member.member_id_public} />
        <div className="mt-4 font-serif text-lg" style={{ color: 'var(--ink)' }}>
          {wallet.member.name}
        </div>
        <div className="font-sans text-xs tracking-[0.28em] mt-1"
             style={{ color: 'var(--ink-muted)' }}>
          {card?.card_number || wallet.member.member_id_public}
        </div>
      </div>
      <div className="mt-4 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
        Show this to the {outlet.name} concierge on arrival.
      </div>
    </div>
  );
}

function HistoryTab({ wallet }) {
  const items = (wallet.vouchers || [])
    .filter((v) => v.status === 'redeemed')
    .sort((a, b) => (b.redeemed_at || '').localeCompare(a.redeemed_at || ''));
  return (
    <div className="pt-8 px-6" data-testid="history-tab">
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-1"
           style={{ color: 'var(--ink-muted)' }}>
        Visit History
      </div>
      <div className="font-serif text-3xl mb-6" style={{ color: 'var(--ink)' }}>
        Enjoyed Experiences
      </div>
      {items.length === 0 ? (
        <EmptyState title="No visits yet"
                    description="Redeemed privileges will appear here as elegant memories."
                    icon={Clock} testId="empty-history" />
      ) : (
        <div className="space-y-3">
          {items.map((v) => (
            <div key={v.id}
                 className="rounded-2xl p-4 flex items-center gap-4"
                 style={{ background: 'var(--surface)',
                          border: '1px solid var(--border-default)',
                          boxShadow: 'var(--shadow-sm)' }}
                 data-testid={`history-${v.id}`}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ background: 'var(--gold-soft)', color: 'var(--gold)' }}>
                <Ticket className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-base" style={{ color: 'var(--ink)' }}>
                  {v.voucher_type_name}
                </div>
                <div className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                  {String(v.redeemed_at || '').slice(0, 10) || 'Recently'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TermsTab({ wallet }) {
  // Prefer the backend-derived `purchases` array (one entry per
  // unique kit sale — the canonical shape for the multi-membership
  // Terms view). Fall back to `cards` when the backend has not yet
  // emitted the new field (older sessions or seed data).
  const purchases = Array.isArray(wallet.purchases) && wallet.purchases.length
    ? wallet.purchases
    : (wallet.cards || [])
        .filter((c) => ['active', 'held', 'expired'].includes(c.status || 'active'))
        .map((c) => ({
          sale_reference: c.id,
          purchased_at:   c.issued_at,
          kit_name:       c.last_kit_name || c.kit_name || c.program_name || 'Membership',
          valid_until:    c.expires_at,
          terms:          c.terms || '',
        }));
  const sorted = [...purchases].sort(
    (a, b) => (b.purchased_at || '').localeCompare(a.purchased_at || '')
  );
  const [openId, setOpenId] = useState(null);

  return (
    <div className="pt-8 px-6 pb-6" data-testid="terms-tab">
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-1"
           style={{ color: 'var(--ink-muted)' }}>
        Terms &amp; Conditions
      </div>
      <div className="font-serif text-3xl mb-6" style={{ color: 'var(--ink)' }}>
        Membership Terms
      </div>

      {sorted.length === 0 && (
        <EmptyState title="No memberships yet"
                    description="Terms will appear here once you activate a membership."
                    icon={FileText} testId="empty-terms" />
      )}

      <div className="space-y-3">
        {sorted.map((p) => {
          const hasTerms = ((p.terms || '').trim().length > 0);
          const isOpen = openId === p.sale_reference;
          return (
            <div key={p.sale_reference}
                 className="rounded-2xl overflow-hidden"
                 style={{ background: 'var(--surface)',
                          border: '1px solid var(--border-default)',
                          boxShadow: 'var(--shadow-sm)' }}
                 data-testid={`terms-card-${p.sale_reference}`}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-serif text-lg" style={{ color: 'var(--ink)' }}>
                      {p.kit_name}
                    </div>
                    <div className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                      Purchased {String(p.purchased_at || '').slice(0, 10)}
                      {p.valid_until ? ` · Valid until ${String(p.valid_until).slice(0, 10)}` : ''}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setOpenId(isOpen ? null : p.sale_reference)}
                  data-testid={`terms-view-${p.sale_reference}`}
                  className="mt-3 h-9 px-4 rounded-lg text-[12px] font-semibold"
                  style={{ background: 'var(--gold-soft)', color: 'var(--gold)' }}>
                  {isOpen ? 'Hide Terms' : 'View Terms'}
                </button>
              </div>
              {isOpen && (
                <div className="px-4 pb-4"
                     data-testid={`terms-body-${p.sale_reference}`}>
                  <div className="border-t pt-3 whitespace-pre-wrap text-[13px] leading-relaxed"
                       style={{ borderColor: 'var(--border-default)',
                                color: 'var(--ink-muted)' }}>
                    {hasTerms
                      ? p.terms
                      : "Terms & Conditions are not available for this membership. Please contact our team for assistance."}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}



function ProfileTab({ wallet, outlet, config, onLogout }) {
  const m = wallet.member;
  return (
    <div className="pt-8 px-6" data-testid="profile-tab">
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-1"
           style={{ color: 'var(--ink-muted)' }}>
        Member Profile
      </div>
      <div className="font-serif text-3xl mb-6" style={{ color: 'var(--ink)' }}>
        {m.name}
      </div>
      <div className="rounded-3xl overflow-hidden"
           style={{ background: 'var(--surface)',
                    border: '1px solid var(--border-default)',
                    boxShadow: 'var(--shadow-sm)' }}>
        <ProfileRow label="Member ID" value={m.member_id_public} mono />
        <ProfileRow label="Phone" value={m.phone} />
        <ProfileRow label="Email" value={m.email} />
        <ProfileRow label="Birthday" value={formatBirthday(m.birthday || m.dob)} />
        <ProfileRow label="Anniversary" value={m.anniversary || '—'} />
      </div>
      <button
        onClick={onLogout}
        data-testid="guest-logout"
        className="mt-6 w-full h-12 rounded-2xl inline-flex items-center justify-center gap-2 font-medium"
        style={{ background: 'var(--surface)',
                 border: '1px solid var(--border-default)',
                 color: 'var(--ink)' }}>
        <LogOut className="w-4 h-4" /> Sign Out
      </button>
      <div className="text-center text-[11px] mt-6"
           style={{ color: 'var(--ink-muted)' }}>
        {outlet.name} · {config?.program_name || 'Privilege Membership'}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------
 * Small helpers
 * ----------------------------------------------------------------- */
function SectionHeader({ title, action }) {
  return (
    <div className="flex items-center justify-between mt-8 mb-4">
      <div className="font-serif text-xl" style={{ color: 'var(--ink)' }}>{title}</div>
      {action}
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <div
      className="rounded-2xl px-4 py-3 text-center"
      style={{
        background: 'var(--surface)',
        // ITER305-refine · light golden edge matches the membership
        // card for a consistent premium-metal card family. 2px so
        // border isn't rounded away on DPR=1.
        border: '2px solid rgba(201, 162, 39, 0.55)',
        boxShadow:
          'inset 0 0 10px rgba(201,162,39,0.12), 0 2px 8px rgba(0,0,0,0.20)',
      }}
      data-testid={`stat-${label.toLowerCase()}`}
    >
      <div className="font-serif text-2xl leading-tight"
           style={{ color: 'var(--ink)' }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] mt-0.5 font-semibold"
           style={{ color: 'var(--gold)' }}>{label}</div>
    </div>
  );
}

function ProfileRow({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5 border-b last:border-b-0"
         style={{ borderColor: 'var(--border-subtle)' }}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold"
           style={{ color: 'var(--ink-muted)' }}>{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''}`}
           style={{ color: 'var(--ink)' }}>{value || '—'}</div>
    </div>
  );
}

function formatBirthday(bday) {
  if (!bday) return '—';
  const s = String(bday);
  if (/^\d{2}-\d{2}$/.test(s)) {
    const [mm, dd] = s.split('-');
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+mm - 1];
    return `${dd} ${m}`;
  }
  return s.slice(0, 10);
}

function VoucherRow({ v, sessionToken, onRefresh, brand }) {
  const [revealBusy, setRevealBusy] = useState(false);
  const [revealed, setRevealed] = useState(null);

  const reveal = async () => {
    setRevealBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/loyalty/guest/wallet/reveal-voucher`,
        { voucher_instance_id: v.id },
        { headers: { 'X-Loyalty-Session': sessionToken } },
      );
      setRevealed(res.data.voucher_code);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reveal failed');
    } finally { setRevealBusy(false); }
  };

  // ITER305 · Share requires reveal + sends the FULL code payload
  // (name + code + validity + outlet). No masked/partial codes ever
  // leave the guest wallet.
  const onShare = async (voucher, code) => {
    if (!code) {
      toast.info('Reveal the code before sharing.');
      return;
    }
    const valid = voucher.valid_until
      ? String(voucher.valid_until).slice(0, 10) : '';
    const lines = [
      voucher.voucher_type_name,
      `Code: ${code}`,
      valid ? `Valid until: ${valid}` : null,
      brand?.outletName ? `Redeem at: ${brand.outletName}` : null,
    ].filter(Boolean);
    const text = lines.join('\n');
    try {
      if (navigator.share) {
        await navigator.share({ title: voucher.voucher_type_name, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('Voucher details copied to clipboard');
      }
    } catch { /* user cancelled */ }
  };

  return (
    <VoucherCard v={v} onReveal={reveal} revealBusy={revealBusy}
                  revealedCode={revealed} onShare={onShare} brand={brand} />
  );
}

/* -----------------------------------------------------------------
 * Placeholder QR — pure SVG. Once we wire an actual QR service on
 * the guest side (e.g. reuse existing membership QR endpoint), swap
 * this for an <img src={qrPngDataUrl} />.
 * ----------------------------------------------------------------- */
function QrPlaceholder({ text }) {
  // Simple deterministic dot grid seeded from the text — visually
  // reads as a QR at a glance until we wire the real endpoint.
  const size = 21;
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0;
  const rand = (i) => {
    const x = Math.sin(hash + i) * 10000;
    return x - Math.floor(x);
  };
  return (
    <div className="mx-auto w-[220px] h-[220px] rounded-2xl p-3"
         style={{ background: '#fff', border: '1px solid var(--border-default)' }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
        {Array.from({ length: size * size }).map((_, i) => {
          const x = i % size, y = Math.floor(i / size);
          // Corner finder patterns
          const isFinder =
            (x < 7 && y < 7) ||
            (x >= size - 7 && y < 7) ||
            (x < 7 && y >= size - 7);
          const on = isFinder
            ? ((x === 0 || x === 6 || y === 0 || y === 6) ||
               (x >= 2 && x <= 4 && y >= 2 && y <= 4))
            : rand(i) > 0.55;
          if (!on) return null;
          return (
            <rect key={i} x={x + 0.1} y={y + 0.1}
                  width="0.8" height="0.8"
                  fill="var(--primary)" />
          );
        })}
      </svg>
    </div>
  );
}

function QrOverlay({ wallet, outlet, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(11, 42, 91, 0.5)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="qr-overlay"
    >
      <motion.div
        initial={{ y: 40, opacity: 0.6 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-8"
        style={{ background: '#fff', boxShadow: 'var(--shadow-hero)' }}
      >
        <button
          onClick={onClose}
          aria-label="Close QR overlay"
          data-testid="qr-overlay-close"
          className="absolute right-4 top-4 p-2 rounded-full hover:bg-black/5"
          style={{ color: 'var(--ink)' }}
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-1"
               style={{ color: 'var(--ink-muted)' }}>Digital Membership</div>
          <div className="font-serif text-2xl mb-5" style={{ color: 'var(--ink)' }}>
            {outlet.name}
          </div>
          <QrPlaceholder text={wallet.cards[0]?.card_number || wallet.member.member_id_public} />
          <div className="mt-4 font-serif text-lg" style={{ color: 'var(--ink)' }}>
            {wallet.member.name}
          </div>
          <div className="font-sans text-xs tracking-[0.3em] mt-1"
               style={{ color: 'var(--ink-muted)' }}>
            {wallet.cards[0]?.card_number || wallet.member.member_id_public}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
